import { type SetStateAction, useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { clearLegacyLensPins, readPinnedLensIds } from '~/pages/AskRichardUnified/lensPins';

import { REPORTING_LE_LENSES_PATH } from '~/consts/reportingLeLensApi';

import useCurrentUser from '~/hooks/useCurrentUser';

import { request } from '~/services/common';

import type { Lens } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

export type PinnedLensSummary = Pick<Lens, 'id' | 'title'>;
type LensPins = { lensIds: Array<string>; lenses?: Array<PinnedLensSummary> };
type UpdatePins = { lensIds: Array<string>; before?: LensPins; revision: number };
export const lensPinsQueryPrefix = ['reporting', 'le-lens', 'pins'] as const;
export const lensPinsQueryKey = (email?: string | null) => [...lensPinsQueryPrefix, email] as const;
const migrated = new Set<string>();
const latestIntent = new Map<string, { lensIds: Array<string>; revision: number }>();
let revision = 0;

const putPins = async (lensIds: Array<string>) =>
  (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENSES_PATH}/pins`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lensIds }),
  })) as LensPins;

/** Import readable browser pins once, after a successful account-scoped server read. */
export async function loadLensPins(email: string, signal?: AbortSignal): Promise<LensPins> {
  let result = (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENSES_PATH}/pins`), { signal })) as LensPins;
  if (!Array.isArray(result.lensIds)) throw new Error('Pinned Lenses are not available in this environment yet.');
  if (!migrated.has(email) && !signal?.aborted) {
    const legacy = [...new Set(readPinnedLensIds(email))].filter((id) => /^[0-9a-f-]{36}$/i.test(id));
    if (!result.lensIds.length && legacy.length) {
      // Older browser pins can point at deleted or no-longer-shared Lenses. One stale pin must not block migration.
      const readable = await Promise.all(
        legacy.map(async (id) => {
          try {
            await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(id)}`), {
              signal,
            });
            return id;
          } catch (error) {
            const status = (error as { status?: number }).status;
            if (status === 403 || status === 404) return null;
            throw error;
          }
        }),
      );
      result = await putPins(readable.filter((id): id is string => id !== null));
      if (!Array.isArray(result.lensIds)) throw new Error('Your browser pins could not be confirmed. Try again.');
    }
    migrated.add(email);
    clearLegacyLensPins(email);
  }
  return result;
}

/** Every surface shares an account-scoped store; queued writes preserve rapid pin/reorder actions. */
export function useLensPinPreferences() {
  const { user } = useCurrentUser();
  const email = user?.email?.trim().toLowerCase();
  const client = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const queryKey = lensPinsQueryKey(email);
  const mutationKey = [...queryKey, 'write'];
  const query = useQuery({
    queryKey,
    enabled: Boolean(email),
    retry: false,
    queryFn: ({ signal }) => loadLensPins(email!, signal),
  });
  const update = useMutation({
    mutationKey,
    scope: { id: `lens-pins:${email}` },
    mutationFn: ({ lensIds }: UpdatePins) => putPins(lensIds),
    onMutate: async ({ lensIds }) => {
      await client.cancelQueries({ queryKey });
      const current = client.getQueryData<LensPins>(queryKey);
      client.setQueryData(queryKey, { ...current, lensIds: latestIntent.get(email ?? '')?.lensIds ?? lensIds });
    },
    onError: (error: Error, variables) => {
      if (latestIntent.get(email ?? '')?.revision === variables.revision)
        client.setQueryData(queryKey, variables.before);
      enqueueSnackbar(error.message || 'Your pins could not be saved. Try again.', { variant: 'error' });
    },
    onSettled: async () => {
      if (client.isMutating({ mutationKey }) === 1) await client.invalidateQueries({ queryKey });
    },
  });
  const setIds = useCallback(
    (value: SetStateAction<Array<string>>) => {
      if (!query.isSuccess) {
        enqueueSnackbar('Your pins could not be loaded. Retry before changing them.', { variant: 'warning' });
        void query.refetch();
        return;
      }
      const before = client.getQueryData<LensPins>(queryKey);
      const lensIds = [...new Set(typeof value === 'function' ? value(before?.lensIds ?? []) : value)];
      const intent = { lensIds, revision: ++revision };
      latestIntent.set(email ?? '', intent);
      client.setQueryData(queryKey, { ...before, lensIds });
      update.mutate({ ...intent, before });
    },
    [client, enqueueSnackbar, query, queryKey, update],
  );
  return { query, setIds };
}
